<template>
  <RevLoadingScreen
    v-if="isLoading && !cart"
    class="bg-surface-default-mid fixed inset-0"
    data-qa="loading-screen"
    :text="''"
  />
  <slot
    v-if="cart"
    :addressList="cart.addressList"
    :billableCountries="cart.billableCountries"
    :isExpressCheckoutEligible="cart.isExpressCheckoutEligible"
    :items="cart.cartItems"
    :lastCollectionPoint="cart.lastCollectionPoint"
    :loading="isLoading"
    :replacements
    :shippableCountries="cart.shippableCountries"
    :summary="cart.summary"
    :userInformation="cart.userInformation"
    @update="handleUpdate"
    @updating="handleUpdating"
  />
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { onMounted, ref, watch } from 'vue'

import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import type {
  CartItem,
  ReplacementResponse,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { useItemReplacement } from '@backmarket/nuxt-layer-checkout/composables/useItemReplacement'
import { usePriceChangeToast } from '@backmarket/nuxt-layer-checkout/composables/usePriceChangeToast'
import { useDiscountStore } from '@backmarket/nuxt-layer-checkout/stores/discountStore'

// default: undefined prevents Vue's boolean casting (absent boolean props default to false otherwise)
const props = withDefaults(defineProps<{ newPriceGrid?: boolean }>(), {
  newPriceGrid: undefined,
})

const $httpFetch = useHttpFetch()
const experiments = useExperiments()
const { setCartPriceWithSwapDiscount } = useBuybackOffer()
const { trackFunnel } = useCheckoutTracking()
const discountStore = useDiscountStore()
const { countryCode } = useMarketplace().market
const { replaceItem } = useItemReplacement()
const { notifyPriceChange } = usePriceChangeToast()
const route = useRoute()

// Cart page route names for e-commerce and in-store flows
const CART_ROUTE_NAMES = [
  'checkout_cart',
  'checkout_in_store_sales_bouygues_telecom_cart',
]

function isCartPage(): boolean {
  return CART_ROUTE_NAMES.includes(route.name as string)
}

function getStoredPostalCode() {
  return localStorage.getItem(`bm.deliveryZipCode.${countryCode}`)
}

// Callers can force a fixed value; falls back to the experiment flag.
const isExperimentNewPriceGrid =
  experiments['experiment.serviceFeeGrid'] === 'newServiceFeeGrid'

const newPriceGrid = props.newPriceGrid ?? isExperimentNewPriceGrid

type Cart = ApiSpecs['/bm/cart']['GET']['response']

const isLoading = ref(false)
const cart = ref<Cart | null>(null)
const replacements = ref<ReplacementResponse | null>(null)

// To be removed with the Promo code revamp - CK-6662
watch(
  () => cart.value?.summary?.totalPrice,
  (newPrice, oldPrice) => {
    if (newPrice !== oldPrice && discountStore.isApplied) {
      discountStore.reset()
    }
  },
)

async function fetchCart() {
  try {
    const storedPostalCode = getStoredPostalCode()
    const response = await $httpFetch('/bm/cart', {
      query: {
        newPriceGrid,
        insuranceOfferFilters: [
          experiments['experiment.annualRecurring'],
          experiments['experiment.monthlyEvergreen'],
          experiments['experiment.insurancePricingSmartphone'],
        ],
        ...(storedPostalCode && { postalCode: storedPostalCode }),
      },
    })
    const responseCamelized = camelizeKeys(response)
    setCartPriceWithSwapDiscount(
      responseCamelized.swap?.ad?.cartPriceWithSwapDiscount ?? null,
    )

    responseCamelized.cartItems.forEach((item) => {
      if (item.previousPrice) {
        notifyPriceChange({
          id: item.listingPublicId,
          previousAmount: item.previousPrice.amount,
          currentAmount: String(item.price),
          source: 'cart_price_change',
          modelName: item.model,
        })
      }
    })

    // Fetch replacements options if some items are out of stock and we're on a cart page
    if (
      isCartPage() &&
      responseCamelized.cartItems.some(({ available }) => available === 'no')
    ) {
      replacements.value = await $httpFetch(
        '/api/checkout/cart/listings/v1/replacement',
        {
          query: {
            listing_ids: responseCamelized.cartItems
              .filter(({ available }) => available === 'no')
              .map((item) => item.listingPublicId)
              .sort()
              .join(','),
          },
        },
      )

      // Auto replacements
      // Build array of auto-replacements to perform
      const autoReplacements = replacements.value
        .map((replacementInfo) => {
          const { original } = replacementInfo
          if (!original) return null

          // Find the corresponding cart item to get additional data
          const cartItem = responseCamelized.cartItems.find(
            (item) => item.listingPublicId === replacementInfo.listingPublicId,
          )

          if (!cartItem) return null

          // Find matching replacement (same grade and offer type)
          const matchingReplacement = replacementInfo.replacements.find(
            (replacement) =>
              replacement.grade.value === original.grade.value &&
              replacement.offerType.value === original.offerType.value,
          )

          if (!matchingReplacement) return null

          return {
            original,
            replacement: matchingReplacement,
            mobilePlan: cartItem.mobilePlan,
            categoryId: cartItem.categoryId,
            modelName: cartItem.model,
            trackingContext: {
              index: responseCamelized.cartItems.indexOf(cartItem),
              list: 'cart_item_auto_replace',
            },
          }
        })
        .filter(
          (replacement): replacement is Exclude<typeof replacement, null> =>
            replacement !== null,
        )

      // Execute all replacements in parallel
      const results = await Promise.all(
        autoReplacements.map((params) =>
          replaceItem(params)
            .then(() => true)
            .catch(() => {
              // Error is already handled in useItemReplacement
              // Silently ignore to allow other replacements to proceed
              return false
            }),
        ),
      )

      autoReplacements.forEach(
        ({ original, replacement, modelName }, index) => {
          if (results[index]) {
            notifyPriceChange({
              id: original.listingPublicId,
              previousAmount: original.price.amount,
              currentAmount: replacement.price.amount,
              source: 'auto_replacement',
              modelName,
              sellerChanged:
                original.sellerPublicId !== replacement.sellerPublicId,
            })
          }
        },
      )

      // Auto replacements happend, let's refresh the cart
      if (results.filter(Boolean).length > 0) {
        return await fetchCart()
      }
    } else {
      replacements.value = null
    }

    return responseCamelized
  } catch {
    return null
  }
}

onMounted(async () => {
  isLoading.value = true
  cart.value = await fetchCart()
  isLoading.value = false
})

/**
 * Applies an optimistic update to the cart items by replacing the item with matching listingId
 * @param currentCartItems - The current array of cart items
 * @param itemToUpdate - The updated cart item to replace in the array
 * @returns A new array with the updated item replaced
 */
function applyOptimisticUpdate(
  cartItems: CartItem[],
  updatedItem: CartItem,
): CartItem[] {
  return cartItems.reduce<CartItem[]>((items, item) => {
    if (item.listingId === updatedItem.listingId) {
      return [...items, updatedItem]
    }

    return [...items, item]
  }, [])
}

async function handleUpdating(updatedItem?: CartItem) {
  const hasOptimisticUpdate = !!updatedItem

  // Set loading state only if we're not doing optimistic updates
  if (!hasOptimisticUpdate) {
    isLoading.value = true
  }

  // Apply optimistic update to immediately reflect changes in the UI
  if (hasOptimisticUpdate && cart.value && updatedItem) {
    cart.value.cartItems = applyOptimisticUpdate(
      cart.value.cartItems,
      updatedItem,
    )
  }
}

async function handleUpdate(callback?: () => void) {
  isLoading.value = true

  try {
    const updatedCart = await fetchCart()

    if (updatedCart) {
      cart.value = updatedCart
      trackFunnel(updatedCart.cartItems, updatedCart.swap.offer)
    }

    if (callback) {
      callback()
    }
  } finally {
    isLoading.value = false
  }
}
</script>
